import type { UseFormReturn } from 'react-hook-form';
import type { NovoItemTributoDto, PerfilTributarioResponse } from '@triade/contracts';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MensagemCampo } from './dados-item';
import { ajudaExcecao, CAMPOS_TAXA_EXCECAO, type CampoExcecao, type FormVigencia } from './tributos-excecao';

/** Campos de excecao e regras do item no modo estruturado (Sessao 51, U2). So apresentacao: os valores vem da API. */

type Form = UseFormReturn<FormVigencia, unknown, NovoItemTributoDto>;

const ROTULO = 'mb-1.5 block text-caption font-medium text-ink-secondary';
const CAMPO_SELECT = 'h-10 w-full rounded-[10px] border border-input bg-card px-2.5 text-small text-ink-body focus-visible:border-primary focus-visible:shadow-focus focus-visible:outline-none aria-[invalid=true]:border-destructive';
const SELO_EXCECAO = 'rounded-full bg-primary/10 px-2 py-0.5 text-caption font-semibold text-primary';

/** PIS, COFINS, ICMS, ISS e IPI: campo vazio usa a tabela; preenchido vira excecao deste item. */
export function CamposExcecaoTributos({ form, perfil }: { form: Form; perfil: PerfilTributarioResponse | null }) {
  return (
    <>
      {CAMPOS_TAXA_EXCECAO.map(({ campo, rotulo }) => (
        <CampoExcecao key={campo} form={form} campo={campo} rotulo={rotulo} perfil={perfil} />
      ))}
    </>
  );
}

function CampoExcecao({ form, campo, rotulo, perfil }: { form: Form; campo: CampoExcecao; rotulo: string; perfil: PerfilTributarioResponse | null }) {
  const id = `v-${campo}`;
  const ajudaId = `${id}-ajuda`;
  const digitado = form.watch(campo);
  const erro = form.formState.errors[campo]?.message;
  const ajuda = ajudaExcecao(campo, digitado, perfil);
  return (
    <div>
      <Label htmlFor={id} className={ROTULO}>
        {rotulo} (%)
      </Label>
      <Input
        id={id}
        inputMode="decimal"
        className="font-mono"
        placeholder="usa a tabela"
        aria-invalid={erro ? true : undefined}
        aria-describedby={[ajudaId, erro ? `${id}-erro` : null].filter(Boolean).join(' ')}
        {...form.register(campo)}
      />
      <div id={ajudaId} className="mt-1 flex flex-wrap items-center gap-1.5 text-caption text-ink-muted">
        {ajuda.excecao && <span className={SELO_EXCECAO}>exceção</span>}
        {ajuda.texto && <span>{ajuda.texto}</span>}
        {ajuda.excecao && (
          <button type="button" onClick={() => form.setValue(campo, '', { shouldDirty: true })} className="rounded-sm text-primary underline-offset-2 hover:underline">
            usar a tabela
          </button>
        )}
      </div>
      <MensagemCampo id={`${id}-erro`} mensagem={erro} />
    </div>
  );
}

/** Regras do item no modo estruturado: importado, reducao de base, natureza da presuncao, regime PIS/COFINS e ST. */
export function RegrasEstruturadas({ form }: { form: Form }) {
  return (
    <>
      <fieldset className="mt-4 rounded-xl border border-dashed border-border p-3.5">
        <legend className="px-1 text-small font-semibold">Regras do item</legend>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-3.5">
          <div className="flex items-end">
            <label htmlFor="v-importado-icms4" className="flex h-10 cursor-pointer items-center gap-2">
              <input id="v-importado-icms4" type="checkbox" className="size-[18px] accent-primary" {...form.register('importadoIcms4')} />
              <span>Importado (ICMS interestadual de 4%)</span>
            </label>
          </div>
          <CampoPercentualSimples form={form} nome="reducaoBaseIcms" rotulo="Redução da base do ICMS (%)" />
          <CampoOpcoes
            form={form}
            nome="naturezaPresuncao"
            rotulo="Natureza para presunção"
            opcoes={[
              ['', 'pelo tipo do item'],
              ['BEM', 'bem'],
              ['SERVICO', 'serviço'],
            ]}
          />
          <CampoOpcoes
            form={form}
            nome="pisCofinsRegime"
            rotulo="Regime PIS/COFINS"
            opcoes={[
              ['', 'normal (padrão)'],
              ['MONOFASICO', 'monofásico'],
              ['ALIQUOTA_ZERO', 'alíquota zero'],
            ]}
          />
        </div>
      </fieldset>
      <fieldset className="mt-4 rounded-xl border border-dashed border-border p-3.5">
        <legend className="px-1 text-small font-semibold">Substituição tributária (ICMS-ST)</legend>
        <p role="note" className="mb-3 rounded-[10px] bg-surface-muted px-3 py-2 text-small text-ink-secondary">
          Com MVA informada, a ST entra no preço (por fora): base = (preço + IPI) × (1 + MVA); em outra UF a MVA é ajustada pela regra do Protocolo ICMS 23/11. A MVA da grade da tabela, por UF de destino, vence a do item.
        </p>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-3.5">
          <CampoPercentualSimples form={form} nome="stMva" rotulo="MVA (%)" />
          <CampoPercentualSimples form={form} nome="stAliquota" rotulo="Alíquota da ST (%)" />
        </div>
      </fieldset>
    </>
  );
}

function CampoPercentualSimples({ form, nome, rotulo }: { form: Form; nome: 'reducaoBaseIcms' | 'stMva' | 'stAliquota'; rotulo: string }) {
  const id = `v-${nome}`;
  const erro = form.formState.errors[nome]?.message;
  return (
    <div>
      <Label htmlFor={id} className={ROTULO}>
        {rotulo}
      </Label>
      <Input id={id} inputMode="decimal" className="font-mono" placeholder="0" aria-invalid={erro ? true : undefined} aria-describedby={erro ? `${id}-erro` : undefined} {...form.register(nome)} />
      <MensagemCampo id={`${id}-erro`} mensagem={erro} />
    </div>
  );
}

function CampoOpcoes({ form, nome, rotulo, opcoes }: { form: Form; nome: 'naturezaPresuncao' | 'pisCofinsRegime'; rotulo: string; opcoes: ReadonlyArray<readonly [string, string]> }) {
  const id = `v-${nome}`;
  const erro = form.formState.errors[nome]?.message;
  return (
    <div>
      <Label htmlFor={id} className={ROTULO}>
        {rotulo}
      </Label>
      <select id={id} className={CAMPO_SELECT} aria-invalid={erro ? true : undefined} {...form.register(nome)}>
        {opcoes.map(([valor, texto]) => (
          <option key={valor} value={valor}>
            {texto}
          </option>
        ))}
      </select>
      <MensagemCampo id={`${id}-erro`} mensagem={erro} />
    </div>
  );
}
